<script generic="TItem" lang="ts" setup>
import type { InputDensity, InputHideDetails, InputVariant, SelectItem, VuetifyRule } from "@/types/props";

const model = defineModel<unknown>("modelValue", { default: null });
const search = defineModel<string>("search", { default: "" });

defineSlots<{
  item(scope: { item: SelectItem<TItem>; props: Record<string, unknown>; index: number }): unknown;
  selection(scope: { item: SelectItem<TItem>; index: number }): unknown;
  "no-data"(): unknown;
  "append-item"(): unknown;
  "prepend-inner"(): unknown;
  "append-inner"(): unknown;
}>();

const props = withDefaults(
  defineProps<{
    items: TItem[];
    id?: string;
    name?: string;
    label?: string;
    placeholder?: string;
    itemTitle?: string;
    itemValue?: string;
    rules?: VuetifyRule[];
    multiple?: boolean;
    chips?: boolean;
    closableChips?: boolean;
    clearable?: boolean;
    returnObject?: boolean;
    required?: boolean;
    disabled?: boolean;
    readonly?: boolean;
    loading?: boolean;
    hideNoData?: boolean;
    hideSelected?: boolean;
    noFilter?: boolean;
    autoSelectFirst?: boolean | "exact";
    color?: string;
    density?: InputDensity;
    variant?: InputVariant;
    hideDetails?: InputHideDetails;
  }>(),
  {
    id: undefined,
    name: undefined,
    label: "",
    placeholder: "",
    itemTitle: "name",
    itemValue: "id",
    rules: () => [],
    multiple: false,
    chips: false,
    closableChips: false,
    clearable: true,
    returnObject: false,
    required: false,
    disabled: false,
    readonly: false,
    loading: false,
    hideNoData: false,
    hideSelected: false,
    noFilter: false,
    autoSelectFirst: false,
    color: "primary",
    density: "compact",
    variant: "outlined",
    hideDetails: "auto",
  },
);
</script>

<template>
  <v-autocomplete
    :id="props.id"
    v-model="model"
    v-model:search="search"
    :auto-select-first="props.autoSelectFirst"
    :chips="props.chips"
    :class="{ required: props.required }"
    :clearable="props.clearable"
    :closable-chips="props.closableChips"
    :color="props.color"
    :density="props.density"
    :disabled="props.disabled"
    :hide-details="props.hideDetails"
    :hide-no-data="props.hideNoData"
    :hide-selected="props.hideSelected"
    :item-title="props.itemTitle"
    :item-value="props.itemValue"
    :items="props.items as unknown[]"
    :label="props.label"
    :loading="props.loading"
    :multiple="props.multiple"
    :name="props.name"
    :no-filter="props.noFilter"
    :placeholder="props.placeholder"
    :readonly="props.readonly"
    :return-object="props.returnObject"
    :rules="props.rules"
    :variant="props.variant"
    autocomplete="off"
    class="omni-autocomplete"
  >
    <template v-if="$slots.item || props.multiple" #item="{ item, props: itemProps, index }">
      <slot :index="index" :item="item as SelectItem<TItem>" :props="itemProps" name="item">
        <v-list-item v-bind="itemProps">
          <template #prepend="{ isSelected }">
            <v-checkbox-btn :model-value="isSelected" :ripple="false" color="primary" tabindex="-1" />
          </template>
        </v-list-item>
      </slot>
    </template>

    <template v-if="$slots.selection" #selection="{ item, index }">
      <slot :index="index" :item="item as SelectItem<TItem>" name="selection" />
    </template>

    <template v-if="$slots['no-data']" #no-data>
      <slot name="no-data" />
    </template>

    <template v-if="$slots['append-item']" #append-item>
      <slot name="append-item" />
    </template>

    <template v-if="$slots['prepend-inner']" #prepend-inner>
      <slot name="prepend-inner" />
    </template>

    <template v-if="$slots['append-inner']" #append-inner>
      <slot name="append-inner" />
    </template>
  </v-autocomplete>
</template>

<style lang="scss" scoped>
.v-input--readonly {
  pointer-events: none;

  &:deep(.v-field__append-inner) {
    display: none;
  }
}
</style>
